import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import {
  LayoutDashboard, Building, DoorOpen, Users, FileText, CreditCard,
  Wrench, BarChart3, Settings, Crown, LogOut, Receipt, Shield, Globe, X, Building2,
} from 'lucide-react';

const Sidebar = ({ type, isOpen, onClose }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { t, locale, setLocale } = useI18n();

  const landlordLinks = [
    { to: '/landlord', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/landlord/properties', icon: Building, label: t('nav.properties') },
    { to: '/landlord/units', icon: DoorOpen, label: t('nav.units') },
    { to: '/landlord/tenants', icon: Users, label: t('nav.tenants') },
    { to: '/landlord/leases', icon: FileText, label: t('nav.leases') },
    { to: '/landlord/payments', icon: CreditCard, label: t('nav.payments') },
    { to: '/landlord/billing', icon: Receipt, label: t('nav.billing') },
    { to: '/landlord/maintenance', icon: Wrench, label: t('nav.maintenance') },
    { to: '/landlord/reports', icon: BarChart3, label: t('nav.reports') },
    { to: '/landlord/audit-logs', icon: Shield, label: t('nav.audit') },
    { to: '/landlord/subscription', icon: Crown, label: t('nav.subscription') },
    { to: '/landlord/settings', icon: Settings, label: t('nav.settings') },
  ];

  const tenantLinks = [
    { to: '/tenant', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/tenant/payments', icon: CreditCard, label: t('nav.payments') },
    { to: '/tenant/maintenance', icon: Wrench, label: t('nav.maintenance') },
    { to: '/tenant/settings', icon: Settings, label: t('nav.settings') },
  ];

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/admin/landlords', icon: Crown, label: t('nav.landlords') },
    { to: '/admin/organisations', icon: Building, label: t('nav.organisations') },
    { to: '/admin/payments', icon: CreditCard, label: 'Payments' },
    { to: '/admin/audit-logs', icon: Shield, label: t('nav.audit') },
  ];

  const links = type === 'admin' ? adminLinks : type === 'tenant' ? tenantLinks : landlordLinks;
  const isActive = (path) => location.pathname === path;

  const langOptions = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'es', label: 'ES' },
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-64 flex flex-col
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ background: '#1a1a2e', borderRight: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Logo */}
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Link to={`/${type}`} className="flex items-center gap-3" onClick={onClose} style={{ textDecoration: 'none' }}>
            <Building2 style={{ width: 24, height: 24, color: '#00a99d' }} />
            <span style={{ fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
              Realty <span style={{ color: '#00a99d' }}>JAG</span>
            </span>
          </Link>
          <button className="lg:hidden" style={{ color: '#94a3b8' }} onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {links.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={onClose}
                data-testid={`nav-${link.to.split('/').pop()}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200"
                style={{
                  color: active ? '#00a99d' : '#94a3b8',
                  background: active ? 'rgba(0,169,157,0.08)' : 'transparent',
                  borderLeft: active ? '2px solid #00a99d' : '2px solid transparent',
                  fontWeight: active ? 600 : 400,
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Language + Logout */}
        <div className="p-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <Globe style={{ width: 16, height: 16, color: '#64748b' }} />
            <div className="flex gap-1">
              {langOptions.map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => setLocale(opt.code)}
                  data-testid={`lang-${opt.code}`}
                  className="px-2 py-1 text-xs rounded transition-all"
                  style={{
                    background: locale === opt.code ? '#00a99d' : 'transparent',
                    color: locale === opt.code ? '#fff' : '#64748b',
                    fontWeight: locale === opt.code ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="nav-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200"
            style={{ color: '#94a3b8' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
